<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { get } from 'svelte/store';
  import type { AppContext, TimelineEvent } from '../app';
  import { type Toast, ToastQueue } from '../core/toastQueue';
  import { lang, t } from '../i18n';
  import { currentDate, flybyTimes, playback } from '../state/stores';

  // Event toasts for the fleet (S3-14, D13): events of every mission crossed while playing forwards ("July 1979 —
  // Voyager 2 flies past Jupiter"), at most one toast every 2 s; what arrives in between is merged ("3 events — July
  // 1979"). Flybys with a title card (the followed spacecraft's, or the selected mission's) are left to the card.
  let { ctx }: { ctx: AppContext } = $props();

  interface Shown {
    id: number;
    toast: Toast<TimelineEvent & { id: string }>;
  }

  const VISIBLE_MS = 4500;
  const queue = new ToastQueue<TimelineEvent & { id: string }>();
  // the event list is fixed for the app's lifetime
  const keyed = $derived(ctx.events.map((e, i) => ({ ...e, id: `${i}` })));
  let toasts = $state<Shown[]>([]);
  let nextId = 0;
  let previous = get(currentDate);

  const unsubscribe = currentDate.subscribe((now) => {
    const before = previous;
    previous = now;
    if (!get(playback).playing || now <= before) {
      // a jump or reverse playback: nothing to announce, and nothing stale left over
      if (now < before || now - before > 3 * 365.25 * 86_400_000) queue.clear();
      return;
    }
    const carded = new Set(get(flybyTimes));
    // a moment with a title card needs no toast besides (landings keep theirs, Sprint 3)
    queue.push(keyed.filter((e) => e.time > before && e.time <= now && !(e.type !== 'landing' && carded.has(e.time))));
  });
  onDestroy(unsubscribe);

  let timer: ReturnType<typeof setInterval> | undefined;
  onMount(() => {
    timer = setInterval(() => {
      const toast = queue.poll(performance.now());
      if (!toast) return;
      const shown = { id: nextId++, toast };
      toasts = [...toasts.slice(-1), shown];
      setTimeout(() => (toasts = toasts.filter((x) => x.id !== shown.id)), VISIBLE_MS);
    }, 100);
  });
  onDestroy(() => clearInterval(timer));

  const month = (ms: number, l: string): string =>
    new Intl.DateTimeFormat(l === 'de' ? 'de-DE' : 'en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
      ms,
    );

  /** "July 1979", or "July – September 1979" for merged events spanning months. */
  function when(events: readonly TimelineEvent[], l: string): string {
    const a = month(events[0]!.time, l);
    const b = month(events[events.length - 1]!.time, l);
    return a === b ? a : `${a} – ${b}`;
  }

  /** The event title, with the spacecraft's name where the title does not already say who it is. */
  function title(e: TimelineEvent, l: 'en' | 'de'): string {
    const text = e.title[l];
    const name = e.spacecraft ? ctx.targets.get(e.spacecraft)?.name[l] : undefined;
    return name && !text.includes(name) ? `${name}: ${text}` : text;
  }
</script>

<div class="toasts" role="status" aria-live="polite" aria-label={$t('toast.label')}>
  {#each toasts as { id, toast } (id)}
    <div class="panel toast" data-testid="toast">
      {#if toast.events.length === 1}
        <p>
          <span class="when">{when(toast.events, $lang)}</span>
          <span class="sep" aria-hidden="true">—</span>
          {title(toast.events[0]!, $lang)}
        </p>
      {:else}
        <p>
          <span class="when">{$t('toast.merged', { n: String(toast.events.length) })}</span>
          <span class="sep" aria-hidden="true">—</span>
          {when(toast.events, $lang)}
        </p>
        <ul>
          {#each toast.events.slice(0, 3) as e (e.id)}
            <li>{title(e, $lang)}</li>
          {/each}
          {#if toast.events.length > 3}
            <li class="more">{$t('toast.more', { n: String(toast.events.length - 3) })}</li>
          {/if}
        </ul>
      {/if}
    </div>
  {/each}
</div>

<style>
  .toasts {
    position: fixed;
    top: calc(var(--hud-top) + 8px);
    left: 50%;
    transform: translateX(-50%);
    width: min(92vw, 520px);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    pointer-events: none;
  }
  .toast {
    margin: 0;
    padding: 10px 14px;
    animation: in var(--dur) var(--ease);
  }
  p {
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: 10px;
  }
  ul {
    margin: 4px 0 0;
    padding: 0;
    list-style: none;
    color: var(--fg-muted);
    font-size: 0.85rem;
  }
  .more {
    font-style: italic;
  }
  .when {
    font-family: var(--font-mono);
    font-weight: 600;
    color: var(--accent);
    white-space: nowrap;
  }
  .sep {
    color: var(--fg-muted);
  }
  @keyframes in {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .toast {
      animation: none;
    }
  }
</style>
